import React from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
  ReferenceLine,
} from 'recharts';
import {
  Box,
  Card,
  CardContent,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Stack,
  Paper,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  ShowChart,
  BarChart as BarChartIcon,
} from '@mui/icons-material';

interface ForecastData {
  forecast_type: string;
  months: number;
  historical_data?: Array<{
    month: string;
    actual_inflow?: number;
    actual_outflow?: number;
    net_cashflow?: number;
    is_historical: boolean;
  }>;
  predictions: Array<{
    month: string;
    predicted_inflow: number;
    predicted_outflow: number;
    net_cashflow: number;
    confidence: number;
    is_historical: boolean;
  }>;
  summary: {
    avg_monthly_inflow: number;
    avg_monthly_outflow: number;
    avg_net_cashflow: number;
    growth_rate: number;
    data_quality: string;
  };
}

interface ForecastChartProps {
  data: ForecastData;
}

const ForecastChart: React.FC<ForecastChartProps> = ({ data }) => {
  const [chartType, setChartType] = React.useState<'line' | 'bar' | 'area'>('area');

  // Combine historical and prediction data for the chart
  const chartData = React.useMemo(() => {
    const historical = data.historical_data || [];
    const predictions = data.predictions || [];

    return [
      ...historical.map((item) => ({
        month: item.month,
        inflow: item.actual_inflow || 0,
        outflow: item.actual_outflow || 0,
        net: item.net_cashflow || 0,
        isHistorical: true,
        confidence: 100,
      })),
      ...predictions.map((item) => ({
        month: item.month,
        inflow: item.predicted_inflow,
        outflow: item.predicted_outflow,
        net: item.net_cashflow,
        isHistorical: false,
        confidence: item.confidence * 100,
      })),
    ];
  }, [data]);

  // Calculate trend
  const trend = React.useMemo(() => {
    if (chartData.length < 2) return 'stable';
    const firstNet = chartData[0].net;
    const lastNet = chartData[chartData.length - 1].net;
    return lastNet > firstNet ? 'up' : lastNet < firstNet ? 'down' : 'stable';
  }, [chartData]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const isHistorical = payload[0].payload.isHistorical;
      const confidence = payload[0].payload.confidence;

      return (
        <Paper sx={{ p: 2, border: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle2" gutterBottom>
            {label} {isHistorical ? '(Historical)' : '(Forecast)'}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Typography
              key={index}
              variant="body2"
              sx={{ color: entry.color, fontWeight: 500 }}
            >
              {entry.name}: ${entry.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          ))}
          {!isHistorical && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Confidence: {confidence.toFixed(0)}%
            </Typography>
          )}
        </Paper>
      );
    }
    return null;
  };

  // Format currency for axis
  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value.toFixed(0)}`;
  };

  const renderChart = () => {
    const commonProps = {
      data: chartData,
      margin: { top: 10, right: 30, left: 0, bottom: 0 },
    };

    switch (chartType) {
      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={formatCurrency} tick={{ fontSize: 12 }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="inflow" name="Inflow" fill="#4caf50" radius={[8, 8, 0, 0]} />
            <Bar dataKey="outflow" name="Outflow" fill="#f44336" radius={[8, 8, 0, 0]} />
            <Bar dataKey="net" name="Net Cashflow" fill="#2196f3" radius={[8, 8, 0, 0]} />
          </BarChart>
        );

      case 'area':
        return (
          <AreaChart {...commonProps}>
            <defs>
              <linearGradient id="colorInflow" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#4caf50" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#4caf50" stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="colorOutflow" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#f44336" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#f44336" stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="colorNet" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#2196f3" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#2196f3" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={formatCurrency} tick={{ fontSize: 12 }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Area
              type="monotone"
              dataKey="inflow"
              name="Inflow"
              stroke="#4caf50"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorInflow)"
            />
            <Area
              type="monotone"
              dataKey="outflow"
              name="Outflow"
              stroke="#f44336"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorOutflow)"
            />
            <Area
              type="monotone"
              dataKey="net"
              name="Net Cashflow"
              stroke="#2196f3"
              strokeWidth={3}
              fillOpacity={1}
              fill="url(#colorNet)"
            />
            {/* Reference line to separate historical from forecast */}
            {data.historical_data && data.historical_data.length > 0 && (
              <ReferenceLine
                x={data.historical_data[data.historical_data.length - 1].month}
                stroke="#666"
                strokeDasharray="5 5"
                label={{ value: 'Forecast →', position: 'top' }}
              />
            )}
          </AreaChart>
        );

      case 'line':
      default:
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={formatCurrency} tick={{ fontSize: 12 }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line
              type="monotone"
              dataKey="inflow"
              name="Inflow"
              stroke="#4caf50"
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="outflow"
              name="Outflow"
              stroke="#f44336"
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="net"
              name="Net Cashflow"
              stroke="#2196f3"
              strokeWidth={3}
              dot={{ r: 5 }}
              activeDot={{ r: 7 }}
            />
            {/* Reference line to separate historical from forecast */}
            {data.historical_data && data.historical_data.length > 0 && (
              <ReferenceLine
                x={data.historical_data[data.historical_data.length - 1].month}
                stroke="#666"
                strokeDasharray="5 5"
                label={{ value: 'Forecast →', position: 'top' }}
              />
            )}
          </LineChart>
        );
    }
  };

  return (
    <Card elevation={2}>
      <CardContent>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              📈 {data.forecast_type.charAt(0).toUpperCase() + data.forecast_type.slice(1)} Forecast
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                label={`${data.months} months`}
                color="primary"
                variant="outlined"
              />
              <Chip
                size="small"
                label={`${(data.summary.growth_rate * 100).toFixed(1)}% growth`}
                icon={trend === 'up' ? <TrendingUp /> : <TrendingDown />}
                color={trend === 'up' ? 'success' : 'error'}
              />
              <Chip
                size="small"
                label={data.summary.data_quality}
                color={data.summary.data_quality === 'good' ? 'success' : 'warning'}
              />
            </Stack>
          </Box>

          {/* Chart type selector */}
          <ToggleButtonGroup
            value={chartType}
            exclusive
            onChange={(_, newType) => newType && setChartType(newType)}
            size="small"
          >
            <ToggleButton value="line">
              <ShowChart fontSize="small" />
            </ToggleButton>
            <ToggleButton value="area">
              <ShowChart fontSize="small" />
            </ToggleButton>
            <ToggleButton value="bar">
              <BarChartIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {/* Chart */}
        <ResponsiveContainer width="100%" height={400}>
          {renderChart()}
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default ForecastChart;

